import clsx from "clsx";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import {
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectIcon,
  SelectPortal,
  SelectContent,
  SelectScrollUpButton,
  SelectViewport,
  SelectScrollDownButton,
  SelectItem,
} from "../shared/select";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../shared/form";
import { Input } from "../shared/input";
import { Textarea } from "../shared/textarea";
import { SharedIcon } from "../shared/shared-icon";

// T212718 - fallback limit for the collection description if the server does not
// send a maxLegnth validation. The server is the authority; this only keeps the
// counter sensible when the field descriptor is incomplete.
const DEFAULT_DESCRIPTION_MAX_LENGTH = 160;

export function EditColorCollectionForm({
  submitHandler,
  data,
  serverErrors,
  setServerErrors,
  closeClickHandler,
}) {
  const formFields = [...data?.fields];
  const formSchema = z.object({
    ...formFields.reduce((acc, field) => {
      if (field.fieldType === "hidden") {
        acc[field.fieldName] = z.any();
      }
      if (field.fieldType === "text") {
        const maxLength = field.fieldValidation.find(
          (validation) => validation.validationType === "maxLegnth"
        );
        const required = field.fieldValidation.find(
          (validation) => validation.validationType === "required"
        );
        acc[field.fieldName] = z
          .string({ message: required?.message })
          .min(1, {
            message: required?.message,
          })
          .max(maxLength?.validationValue ?? 255, {
            message: maxLength?.message,
          });
      }
      // T212718 - the description is optional, so there is no min length rule here.
      // An empty string is a valid value and must submit without error.
      if (field.fieldType === "textarea") {
        const maxLength = field.fieldValidation?.find(
          (validation) => validation.validationType === "maxLegnth"
        );
        acc[field.fieldName] = z
          .string()
          .max(
            Number(maxLength?.validationValue) ||
              DEFAULT_DESCRIPTION_MAX_LENGTH,
            { message: maxLength?.message }
          )
          .optional();
      }
      if (field.fieldType === "select") {
        const required = field.fieldValidation.find(
          (validation) => validation.validationType === "required"
        );
        acc[field.fieldName] = z
          .string({ message: required?.message })
          .min(1, { message: required?.message });
      }
      return acc;
    }, {}),
  });
  const defaultFormValues = formFields.reduce((acc, field) => {
    // A textarea must never be handed a null value or React flips it between
    // uncontrolled and controlled.
    acc[field.fieldName] =
      field.fieldType === "textarea" ? field.fieldValue ?? "" : field.fieldValue;
    return acc;
  }, {});
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...defaultFormValues,
    },
  });

  const submitFormHandler = (body) => {
    submitHandler(body);
  };

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(submitFormHandler)}>
          {formFields?.length > 0 &&
            formFields.map((formField) => {
              if (formField.fieldType === "hidden" && !!formField.fieldValue) {
                return (
                  <FormField
                    key={formField.fieldName}
                    control={form.control}
                    name={formField.fieldName}
                    render={({ field }) => (
                      <FormItem style={{ visibility: "hidden" }}>
                        <FormLabel>{formField.fieldLabel}</FormLabel>
                        <FormControl>
                          <input
                            type="hidden"
                            style={{ visibility: "hidden" }}
                            {...field}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                );
              }

              if (formField.fieldType === "text") {
                return (
                  <FormField
                    key={formField.fieldName}
                    control={form.control}
                    name={formField.fieldName}
                    render={({ field, fieldState }) => (
                      <FormItem className="shadcn-input-wrapper shadcn-input-wrapper--mt-24">
                        <FormLabel className="shadcn-input-label">
                          {formField.fieldLabel}
                        </FormLabel>
                        <FormControl>
                          <Input
                            className={clsx(
                              "shadcn-input",
                              fieldState?.error && "shadcn-invalid"
                            )}
                            type="text"
                            {...field}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                              setServerErrors({});
                            }}
                          />
                        </FormControl>
                        <FormMessage className="shadcn-input-error-message shadcn-input-error-message--pl-30">
                          {fieldState?.error?.message}
                          {Array.isArray(serverErrors?.[formField.fieldName])
                            ? serverErrors?.[formField.fieldName]?.join(" ")
                            : serverErrors?.[formField.fieldName]}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                );
              }

              if (formField.fieldType === "textarea") {
                const maxLength =
                  Number(
                    formField.fieldValidation?.find(
                      (validation) => validation.validationType === "maxLegnth"
                    )?.validationValue
                  ) || DEFAULT_DESCRIPTION_MAX_LENGTH;

                return (
                  <FormField
                    key={formField.fieldName}
                    control={form.control}
                    name={formField.fieldName}
                    render={({ field, fieldState }) => (
                      <FormItem className="shadcn-input-wrapper shadcn-input-wrapper--mt-16">
                        <FormLabel className="shadcn-input-label">
                          {formField.fieldLabel}
                        </FormLabel>
                        <FormControl>
                          <Textarea
                            className={clsx(
                              "shadcn-input",
                              "shadcn-textarea",
                              fieldState?.error && "shadcn-invalid"
                            )}
                            data-testid="collection-description-input"
                            rows={3}
                            maxLength={maxLength}
                            placeholder={formField.fieldPlaceholder}
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) => {
                              field.onChange(e.target.value);
                              setServerErrors({});
                            }}
                          />
                        </FormControl>
                        <FormDescription
                          className="shadcn-input-counter"
                          data-testid="collection-description-counter"
                        >
                          {`${(field.value ?? "").length}/${maxLength}`}
                        </FormDescription>
                        <FormMessage className="shadcn-input-error-message shadcn-input-error-message--pl-30">
                          {fieldState?.error?.message}
                          {Array.isArray(serverErrors?.[formField.fieldName])
                            ? serverErrors?.[formField.fieldName]?.join(" ")
                            : serverErrors?.[formField.fieldName]}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                );
              }

              if (formField.fieldType === "select") {
                return (
                  <FormField
                    key={formField.fieldName}
                    control={form.control}
                    name={formField.fieldName}
                    render={({ field, fieldState }) => (
                      <FormItem className="shadcn-input-wrapper shadcn-input-wrapper--mt-16">
                        <FormLabel className="shadcn-input-label">
                          {formField.fieldLabel}
                        </FormLabel>
                        <FormControl>
                          <SelectRoot
                            onValueChange={(value) => {
                              field.onChange(value);
                              setServerErrors({});
                            }}
                            defaultValue={field.value}
                          >
                            <SelectTrigger
                              className={clsx(
                                "shadcn-select-trigger",
                                fieldState?.error && "shadcn-invalid"
                              )}
                            >
                              <SelectValue
                                className="shadcn-select-value"
                                placeholder={
                                  formField?.fieldOptions?.find(
                                    (fieldOption) =>
                                      fieldOption.optionValue === ""
                                  )?.optionLabel || "Select an option…"
                                }
                              />
                              <SelectIcon className="shadcn-select-icon">
                                <SharedIcon
                                  iconName="#sprite-icon-dropdown-arrow"
                                  style={{ width: "14px", height: "14px" }}
                                />
                              </SelectIcon>
                            </SelectTrigger>
                            <SelectPortal>
                              <SelectContent className="shadcn-select-content">
                                <SelectScrollUpButton />
                                <SelectViewport className="shadcn-select-viewport">
                                  {formField.fieldOptions
                                    .filter((option) => !!option.optionValue)
                                    .map((option, index) => (
                                      <SelectItem
                                        value={option.optionValue}
                                        key={index}
                                      >
                                        {option.optionLabel}
                                      </SelectItem>
                                    ))}
                                </SelectViewport>
                                <SelectScrollDownButton />
                              </SelectContent>
                            </SelectPortal>
                          </SelectRoot>
                        </FormControl>
                        <FormMessage className="shadcn-input-error-message shadcn-input-error-message--pl-30">
                          {fieldState?.error?.message}
                          {Array.isArray(serverErrors?.[formField.fieldName])
                            ? serverErrors?.[formField.fieldName]?.join(" ")
                            : serverErrors?.[formField.fieldName]}
                        </FormMessage>
                      </FormItem>
                    )}
                  />
                );
              }
              return <></>;
            })}
          <div className="edit-color-collection-actions-wrapper">
            {data?.mainAction && (
              <button
                className="color-collection-action color-collection-action--main color-collection-action--order-main color-collection-action--grow"
                type="submit"
              >
                {data?.mainAction?.linkLabel}
              </button>
            )}
            {data?.secondaryAction && (
              <button
                className="color-collection-action color-collection-action--secondary color-collection-action--order-secondary color-collection-action--grow"
                type="button"
                onClick={closeClickHandler}
              >
                {data?.secondaryAction?.linkLabel}
              </button>
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
